// Service
import { QueuesService } from '../../../service/queues.service';
// Model
import { QueueModel, QueueOptionSets, RolesModel, SaveQueueEditorPayload } from '../../../model/queue.model';
// Angular
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogModes, DialogResult } from '../../../enum/dialog.enum';

@Component({
    selector: 'cv-queue-editor-dialog',
    templateUrl: 'queue-editor.dialog.component.html',
    styleUrls: ['queue-editor.dialog.component.scss'],
})
export class QueueEditorDialogComponent implements OnInit {
    public dialogMode: DialogModes;
    public editQueueOriginalData: QueueModel;
    public queueOptionSets: QueueOptionSets[] = [];
    queueForm!: FormGroup;
    roles: RolesModel[] = [];
    constructor(
        private queueService: QueuesService,
        private fb: FormBuilder,
        private messagesService: MessageService,
        private dialogRef: DynamicDialogRef,
        private config: DynamicDialogConfig
    ) {}

    ngOnInit() {
        this.setDialogMode(this.config.data.dialogMode ?? DialogModes.CREATE);
        this.initQueueForm();
        this.loadOptionSets();
        this.loadQueueRoles();
    }

    /**
     * Dialog mode 
     */
    setDialogMode(mode: DialogModes): void {
        this.dialogMode = mode;
    }

    /**
     * Returns the current queue logic
     * @returns {string}
     */
    getCurrentQueueLogic(): string {
        return this.config.data?.queueData?.filters ?? '';
    }

    /**
     * Inits the queue form with the controls.
     */
    initQueueForm() {
        this.queueForm = this.fb.group({
          name: ['', [Validators.required, Validators.maxLength(1000)]],
          priority: [null, [Validators.required, Validators.max(1000000)]],
          description: ['', [Validators.required, Validators.maxLength(1000)]],
          assignedToRoles: [[], [Validators.required]],
          filters: [null, [Validators.required]],
        });
      
        if (this.dialogMode === DialogModes.EDIT) {
          const existingQueue = this.config?.data?.queueData as {
            name: string;
            description: string;
            assignedToRoles: string[] | null;
            filters: string | object | null;
            priority: number;
          };
      
          // normalize filters to the same type you use in the form (string or null)
          const filtersValue =
            existingQueue?.filters == null
              ? null
              : typeof existingQueue.filters === 'string'
                ? existingQueue.filters
                : JSON.stringify(existingQueue.filters);
      
          this.queueForm.patchValue({
            name: existingQueue?.name ?? '',
            description: existingQueue?.description ?? '',
            assignedToRoles: Array.isArray(existingQueue?.assignedToRoles) ? existingQueue!.assignedToRoles : [],
            filters: filtersValue, // keep as string for the required validator,
            priority: existingQueue.priority ?? 0
          });
          // if you want controls to show as pristine after patch:
          this.queueForm.markAsPristine();
          this.queueForm.updateValueAndValidity();
        }
      }

    setJSONFilter(filters: string): void {
        this.queueForm.get('filters')!.setValue(filters);
        this.queueForm.get('filters')!.markAsDirty();
    }

    saveQueue() {}
    /**
     * Load the roles used in the queue editor.
     */
    async loadQueueRoles() {
        try {
            this.roles = (await lastValueFrom(this.queueService.getQueueRoles())).data;
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Load option sets for filter editor
     */
    async loadOptionSets() {
        try {
            this.queueOptionSets = await lastValueFrom(
                this.queueService.getQueueEditorOptionSets(),
            );
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Saves the queue JSON logic.
     * @param logic
     */
    saveQueueLogic(logic: string) {
        this.queueForm.patchValue({ filters: logic || null });
        this.queueForm.get('filters')!.markAsDirty();
        this.queueForm.get('filters')!.updateValueAndValidity();
    }

    async save() {
        if (this.queueForm.invalid) {
            this.queueForm.markAllAsTouched();
            return;
        }

        const formValue = this.queueForm.value;
        const savePayload: SaveQueueEditorPayload = {
            name: formValue.name,
            description: formValue.description,
            assignedToRoles: formValue.assignedToRoles || [],
            filters: formValue.filters
                ? typeof formValue.filters === 'string'
                    ? formValue.filters
                    : JSON.stringify(formValue.filters)
                : '',
            priority: formValue.priority,
        };

        try {
            if(this.dialogMode === DialogModes.CREATE) {
                await lastValueFrom(this.queueService.saveQueueEditor(savePayload));
            } else {
                const originalQueueId = this.config.data?.queueData?.id ?? null;
                await lastValueFrom(this.queueService.editQueue(savePayload, originalQueueId));
            }
            this.messagesService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'The Queue was saved successfully',
            });
            this.dialogRef.close(DialogResult.SUCCESS);
        } catch (error) {
            console.error('Error saving queue:', error);
        }
    }
    public cancel() {
        this.dialogRef.close();
    }
}
